import React, { useState, useRef } from 'react';
import { AssetType } from '@/firebase/assetService';
import { locationService, Location } from '@/firebase/locationService';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useNotification } from '@/contexts/NotificationContext';
import { usePreview } from '@/contexts/PreviewContext';
import { Upload, X, FileSpreadsheet, FileText, Sparkles, Check } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { extractTableFromPDF, PDFTableExtraction } from '@/services/geminiService';

interface BulkUploadAssetProps {
  assetType: AssetType;
  onSubmit: () => void;
  onCancel: () => void;
}

const BulkUploadAsset: React.FC<BulkUploadAssetProps> = ({ assetType, onSubmit, onCancel }) => {
  const { currentOrganization } = useOrganization();
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const { previewState, isPreviewMode } = usePreview();
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [isExtractingPDF, setIsExtractingPDF] = useState(false);
  const [extractedData, setExtractedData] = useState<PDFTableExtraction | null>(null);
  const [assetNameColumn, setAssetNameColumn] = useState<string>('');
  const [locationColumn, setLocationColumn] = useState<string>('');
  const [uploadMode, setUploadMode] = useState<'csv' | 'pdf'>('pdf');
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    loadLocations();
  }, [currentOrganization?.id, isPreviewMode, previewState?.previewType, previewState?.previewUserId]);

  const loadLocations = async () => {
    setIsLoadingLocations(true);
    try {
      let effectiveUserId: string | undefined;
      let userLocationIds: string[] | undefined;
      let userRole: string | undefined;

      if (isPreviewMode && previewState) {
        // Use previewed user's credentials so support can see the same locations as the previewed org
        if (previewState.previewType === 'user' && previewState.previewUserId) {
          effectiveUserId = previewState.previewUserId;
          userRole = previewState.role;
          userLocationIds = previewState.locationIds;
        } else {
          // Preview by role (e.g. admin): show all locations for the organization
          effectiveUserId = user?.id ?? '';
          userRole = 'admin';
          userLocationIds = undefined;
        }
      } else if (user?.id) {
        effectiveUserId = user.id;
        try {
          const { doc, getDoc } = await import('firebase/firestore');
          const { db } = await import('@/firebase/config');
          const userRef = doc(db, 'users', user.id);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const userData = userSnap.data() as any;
            userLocationIds = userData?.locationIds;
            userRole = userData?.role;
          }
        } catch (error) {
          console.error('Error fetching user data for bulk upload locations:', error);
        }
      }

      const result = await locationService.getAccessibleLocations(
        currentOrganization?.id,
        effectiveUserId,
        userRole,
        userLocationIds
      );

      if (result.success && result.data) {
        const validLocations = result.data.filter(loc => loc.id && loc.name);
        setLocations(validLocations);

        if (validLocations.length === 1 && !selectedLocationId) {
          setSelectedLocationId(validLocations[0].id || '');
        } else if (
          selectedLocationId &&
          !validLocations.some(location => location.id === selectedLocationId)
        ) {
          setSelectedLocationId('');
        }
      } else {
        setLocations([]);
      }
    } catch (error) {
      console.error('Error loading accessible locations for bulk upload:', error);
      setLocations([]);
    } finally {
      setIsLoadingLocations(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!(isLoading || isExtractingPDF || (uploadMode === 'csv' && !selectedLocationId))) {
      setIsDraggingOver(true);
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!(isLoading || isExtractingPDF || (uploadMode === 'csv' && !selectedLocationId))) {
      setIsDraggingOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
    if (isLoading || isExtractingPDF || (uploadMode === 'csv' && !selectedLocationId)) return;
    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;
    const fakeEvent = { target: { files, value: '' } } as React.ChangeEvent<HTMLInputElement>;
    await handleFileUpload(fakeEvent);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isCSV = file.name.toLowerCase().endsWith('.csv');
    const isPDF = file.name.toLowerCase().endsWith('.pdf');

    if (!isCSV && !isPDF) {
      showNotification('Please upload a CSV or PDF file', 'error');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Handle PDF upload - location not required until import
    if (isPDF) {
      // Validate file type
      if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
        showNotification('Please upload a valid PDF file', 'error');
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      setIsExtractingPDF(true);
      let extractionSuccess = false;
      try {
        console.log('Starting PDF extraction for file:', file.name, file.size, 'bytes');
        const extraction = await extractTableFromPDF(file);
        console.log('PDF extraction result:', extraction);
        
        if (!extraction || !extraction.columns || extraction.columns.length === 0) {
          throw new Error('No table data found in PDF. Please ensure the PDF contains a table with asset information.');
        }
        
        setExtractedData(extraction);
        
        // Auto-select detected columns
        if (extraction.detectedAssetNameColumn) {
          setAssetNameColumn(extraction.detectedAssetNameColumn);
        }
        if (extraction.detectedLocationColumn) {
          setLocationColumn(extraction.detectedLocationColumn);
        }
        
        setUploadMode('pdf');
        extractionSuccess = true;
        showNotification(`PDF processed successfully! Found ${extraction.rows.length} rows. Please map columns and select location below.`, 'success');
      } catch (error: any) {
        console.error('PDF extraction error:', error);
        const errorMessage = error?.message || 'Failed to extract data from PDF. Please ensure the PDF contains a readable table.';
        showNotification(`Error extracting PDF: ${errorMessage}`, 'error');
        setExtractedData(null);
        setAssetNameColumn('');
        setLocationColumn('');
        extractionSuccess = false;
      } finally {
        setIsExtractingPDF(false);
        // Only reset file input on error
        if (!extractionSuccess && fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
      return;
    }

    // For CSV/Excel, location is required before upload
    if (!selectedLocationId) {
      showNotification('Please select a location first', 'error');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setIsLoading(true);
    try {
      const reader = new FileReader();
      
      if (file.name.toLowerCase().endsWith('.csv')) {
        // Handle CSV file
        reader.onload = async (e) => {
          try {
            const text = e.target?.result as string;
            const lines = text.split('\n').filter(line => line.trim());
            
            if (lines.length < 2) {
              throw new Error('CSV must have at least a header row and one data row');
            }
            
            // Parse CSV - simple CSV parsing
            const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
            const assetNameIndex = headers.findIndex(h => h.toLowerCase().includes('asset name'));
            const assetLocationIndex = headers.findIndex(h => h.toLowerCase().includes('asset location'));
            
            if (assetNameIndex === -1) {
              throw new Error('CSV must have an "Asset Name" column');
            }

            const selectedLocation = locations.find(l => l.id === selectedLocationId);
            if (!selectedLocation) {
              throw new Error('Selected location not found');
            }

            const assetsToAdd: any[] = [];
            for (let i = 1; i < lines.length; i++) {
              const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
              if (values.length === 0) continue;
              
              const assetName = values[assetNameIndex]?.trim();
              const assetLocation = assetLocationIndex !== -1 ? values[assetLocationIndex]?.trim() : '';
              
              if (!assetName) continue;
              
              assetsToAdd.push({
                assetTypeId: assetType.id!,
                locationId: selectedLocationId,
                assetName,
                assetLocation: assetLocation || undefined,
                ...(currentOrganization?.id && { organizationId: currentOrganization.id })
              });
            }

            if (assetsToAdd.length === 0) {
              throw new Error('No valid assets found in CSV');
            }

            // Import assetService dynamically
            const { assetService } = await import('@/firebase/assetService');
            const result = await assetService.addAssetsBulk(assetsToAdd, currentOrganization?.id);
            
            if (result.success) {
              showNotification(`Successfully added ${assetsToAdd.length} assets`, 'success');
              onSubmit();
            } else {
              throw new Error(result.error || 'Failed to upload assets');
            }
          } catch (error: any) {
            showNotification(`Error parsing CSV: ${error.message}`, 'error');
          } finally {
            setIsLoading(false);
          }
        };
        reader.readAsText(file);
      } else {
        showNotification('Please use CSV format', 'error');
        setIsLoading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    } catch (error: any) {
      showNotification(`Error: ${error.message}`, 'error');
      setIsLoading(false);
    }
  };

  const handlePDFImport = async () => {
    if (!extractedData || !assetNameColumn || !selectedLocationId) {
      showNotification('Please select asset name column and location', 'error');
      return;
    }

    setIsLoading(true);
    try {
      const selectedLocation = locations.find(l => l.id === selectedLocationId);
      if (!selectedLocation) {
        throw new Error('Selected location not found');
      }

      const assetsToAdd: any[] = [];
      for (const row of extractedData.rows) {
        const assetName = row[assetNameColumn]?.trim();
        if (!assetName) continue;

        const assetLocation = locationColumn ? row[locationColumn]?.trim() : '';

        assetsToAdd.push({
          assetTypeId: assetType.id!,
          locationId: selectedLocationId,
          assetName,
          assetLocation: assetLocation || undefined,
          ...(currentOrganization?.id && { organizationId: currentOrganization.id }),
        });
      }

      if (assetsToAdd.length === 0) {
        throw new Error('No valid assets found in extracted data');
      }

      const { assetService } = await import('@/firebase/assetService');
      const result = await assetService.addAssetsBulk(assetsToAdd, currentOrganization?.id);

      if (result.success) {
        showNotification(`Successfully imported ${assetsToAdd.length} assets from PDF`, 'success');
        onSubmit();
      } else {
        throw new Error(result.error || 'Failed to import assets');
      }
    } catch (error: any) {
      showNotification(`Error importing assets: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Asset Type
        </label>
        <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-900">
          {assetType.name}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Location <span className="text-red-500">*</span>
        </label>
        <select
          value={selectedLocationId}
          onChange={(e) => setSelectedLocationId(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          required
          disabled={isLoading || isLoadingLocations || locations.length === 0}
        >
          <option value="">
            {isLoadingLocations ? 'Loading locations...' : 'Select a location...'}
          </option>
          {locations.map(location => (
            <option key={location.id} value={location.id}>
              {location.name}
            </option>
          ))}
        </select>
        {!isLoadingLocations && locations.length === 0 && (
          <p className="text-xs text-red-600 mt-2">
            No locations available. You may not be assigned to any locations or locations have not been created.
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Upload File <span className="text-red-500">*</span>
        </label>
        <div className="flex gap-2 mb-3">
          <button
            type="button"
            onClick={() => {
              setUploadMode('pdf');
              setExtractedData(null);
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}
            className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
              uploadMode === 'pdf'
                ? 'bg-primary-50 border-primary-500 text-primary-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <FileText className="w-4 h-4 inline mr-1" />
            PDF Report
          </button>
          <button
            type="button"
            onClick={() => {
              setUploadMode('csv');
              setExtractedData(null);
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}
            className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
              uploadMode === 'csv'
                ? 'bg-primary-50 border-primary-500 text-primary-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <FileSpreadsheet className="w-4 h-4 inline mr-1" />
            CSV
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          {uploadMode === 'csv'
            ? 'CSV format with columns: "Asset Name", "Asset Location" (optional)'
            : 'Upload a PDF report with asset/equipment data. AI will extract table data automatically.'}
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept={uploadMode === 'csv' ? '.csv' : '.pdf'}
          onChange={handleFileUpload}
          className="hidden"
          disabled={isLoading || isExtractingPDF}
        />
        <div
          role="button"
          tabIndex={0}
          onClick={() => !(isLoading || isExtractingPDF || (uploadMode === 'csv' && !selectedLocationId)) && fileInputRef.current?.click()}
          onKeyDown={(e) => e.key === 'Enter' && !(isLoading || isExtractingPDF || (uploadMode === 'csv' && !selectedLocationId)) && fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`w-full px-4 py-3 border-2 border-dashed rounded-lg transition-colors flex items-center justify-center gap-2 ${
            isLoading || isExtractingPDF || (uploadMode === 'csv' && !selectedLocationId)
              ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
              : isDraggingOver
              ? 'border-primary-500 bg-primary-50 cursor-pointer'
              : 'border-gray-300 hover:border-primary-400 hover:bg-primary-50 cursor-pointer'
          }`}
        >
          {isExtractingPDF ? (
            <>
              <Sparkles className="w-5 h-5 text-orange-500 animate-spin" />
              <span className="text-sm text-orange-600 font-medium animate-pulse">AI analyzing PDF...</span>
            </>
          ) : (
            <>
              <Upload className="w-5 h-5 text-gray-400" />
              <span className="text-sm text-gray-600">
                {isLoading ? 'Processing...' : isDraggingOver ? 'Drop files here' : `Click or drag to upload ${uploadMode === 'csv' ? 'CSV' : 'PDF'}`}
              </span>
            </>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-2 text-center">
          {uploadMode === 'csv'
            ? 'Example: Header row with "Asset Name" and optional "Asset Location" columns'
            : 'AI will analyze the PDF and extract table data for you to review'}
        </p>

        {/* Enhanced AI Loading State */}
        {isExtractingPDF && (
          <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex items-center justify-center gap-3">
              <Sparkles className="w-6 h-6 text-orange-500 animate-spin" />
              <div className="text-center">
                <p className="text-sm font-medium text-orange-900">AI is analyzing your PDF</p>
                <p className="text-xs text-orange-700 mt-1">Extracting table data and identifying columns...</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* PDF Column Mapping UI */}
      {extractedData && extractedData.columns.length > 0 && (
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <div className="flex items-center gap-2 mb-4">
            <Check className="w-5 h-5 text-green-600" />
            <div>
              <h3 className="text-sm font-semibold text-gray-900">PDF Data Extracted</h3>
              <p className="text-xs text-gray-600">{extractedData.summary}</p>
              <p className="text-xs text-gray-500 mt-1">
                Found {extractedData.rows.length} rows with {extractedData.columns.length} columns
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Asset Name Column <span className="text-red-500">*</span>
              </label>
              <select
                value={assetNameColumn}
                onChange={(e) => setAssetNameColumn(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              >
                <option value="">Select column...</option>
                {extractedData.columns.map((col) => (
                  <option key={col} value={col}>
                    {col}
                  </option>
                ))}
              </select>
              {extractedData.detectedAssetNameColumn && !assetNameColumn && (
                <p className="text-xs text-blue-600 mt-1">
                  💡 AI detected: "{extractedData.detectedAssetNameColumn}"
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location Column (Optional)
              </label>
              <select
                value={locationColumn}
                onChange={(e) => setLocationColumn(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">None / Use selected location</option>
                {extractedData.columns.map((col) => (
                  <option key={col} value={col}>
                    {col}
                  </option>
                ))}
              </select>
              {extractedData.detectedLocationColumn && !locationColumn && (
                <p className="text-xs text-blue-600 mt-1">
                  💡 AI detected: "{extractedData.detectedLocationColumn}"
                </p>
              )}
            </div>

            {/* Preview of first few rows */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preview (first 3 rows)
              </label>
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-48">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-100">
                      <tr>
                        {extractedData.columns.map((col) => (
                          <th
                            key={col}
                            className={`px-2 py-1.5 text-left font-medium ${
                              col === assetNameColumn
                                ? 'bg-blue-100 text-blue-900'
                                : col === locationColumn
                                ? 'bg-green-100 text-green-900'
                                : 'text-gray-700'
                            }`}
                          >
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {extractedData.rows.slice(0, 3).map((row, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          {extractedData.columns.map((col) => (
                            <td key={col} className="px-2 py-1.5 text-gray-600">
                              {row[col] || '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={handlePDFImport}
              disabled={!assetNameColumn || !selectedLocationId || isLoading}
              className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Importing {extractedData.rows.length} assets...</span>
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  <span>Import {extractedData.rows.length} Assets</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}

      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          disabled={isLoading || isExtractingPDF}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default BulkUploadAsset;



